import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteConfirmDialog } from '@components/DeleteConfirmDialog';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useBillingBasePath, useClaimSubmission } from '../../store/hooks';

interface DeleteClaimButtonProps {
  claimId: string;
  disabled?: boolean;
  iconSize?: 'small' | 'medium' | 'large';
}

export const DeleteClaimButton: React.FC<DeleteClaimButtonProps> = ({
  claimId,
  disabled = false,
  iconSize = 'medium',
}) => {
  const navigate = useNavigate();
  const basePath = useBillingBasePath();
  const { enqueueSnackbar } = useSnackbar();
  const { deleteClaim, isSubmitting: isDeleting } = useClaimSubmission();
  const { billing: billingMetrics } = useEventTracking();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDelete = async () => {
    const result = await deleteClaim(claimId);
    setDeleteDialogOpen(false);
    if (result.success) {
      // Navigate away immediately — replace so back button doesn't return to deleted claim
      navigate(`${basePath}?tab=claims`, { replace: true });

      // Track claim deletion
      billingMetrics.deleteClaim(claimId, {
        claim_status: 'draft', // Claims can only be deleted if they're drafts
      });

      enqueueSnackbar('Claim deleted successfully', { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to delete claim', { variant: 'error' });
    }
  };

  const iconSizeMap = {
    small: '1rem',
    medium: '1.2rem',
    large: '1.5rem',
  };

  return (
    <>
      <Tooltip title="Delete Claim" placement="bottom" arrow>
        <span>
          <IconButton
            onClick={() => setDeleteDialogOpen(true)}
            disabled={disabled || isDeleting}
            size={iconSize}
          >
            <DeleteIcon sx={{ fontSize: iconSizeMap[iconSize] }} />
          </IconButton>
        </span>
      </Tooltip>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        type="claim"
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};